// Notifications.tsx
import { Empty } from 'antd';
import Title from 'antd/es/typography/Title';
import { notificationApi } from 'Api/notification';
import { Loader } from 'components/Global';
import Buttons from 'components/Global/Buttons';
import SearchComponent from 'components/Global/SearchComponent';
import { useState } from 'react';
import { NoNotificationSvg } from 'utils/svgs';
import { invalidText, toastText } from 'utils/utils';
import AddPushNotificationModal from '../addPushNotifcationApprove';
import DeletePushNotificationModal from '../deletePushNotificationApprove';
import EmployeeNotificationModal from '../EmployeeAndTimeSheetNotifications';
import UpdateNotificationModal from '../updateNotification';
import styles from './index.module.scss';

// Custom debounce hook

type Props = {
	onClose: () => void;
	open: boolean;
	notificationData: any[];
	loading: boolean;
	fetchAllNotifications: () => void;
	changePage: () => void;
	handleSearch: (value: any) => void;
	setNotifications: any;
	searchValue: string;
	dismissAll: () => void;
};

const Notifications = (props: Props) => {
	const {
		onClose,
		loading,
		changePage,
		handleSearch,
		notificationData: notifications,
		setNotifications,
		searchValue,
		dismissAll,
	} = props;

	const [loadingNotifications, setLoadingNotifications] = useState<
		Record<string, boolean>
	>({});
	const [notificationModalOpen, setNotificationModalOpen] = useState(false);
	const [approvalId, setApprovalId] = useState<string | null>(null);
	const [notificationId, setNotificationId] = useState<string | null>(null);
	const [isDelete, setIsDelete] = useState(false);
	const [deleteNotificationModal, setDeleteNotificationModal] =
		useState(false);
	const [employeeNotificationModal, setEmployeeNotificationModal] =
		useState(false);

	const [editNotificationModal, setEditNotificationModal] = useState(false);

	const handleNotificationAction = async (data: any) => {
		const notificationType = data.notification.notificationType;

		if (!notificationType) return null;

		if (notificationType === 'PUSH') {
			if (data.notification.isContractExpiry) {
				await dismissContractExpiryNotification(data.id, data.notification);
			} else if (data.notification.isNhimaEligibility) {
				await dismissNhimaEligibilityNotification(
					data.id,
					data.notification
				);
			} else {
				dismissNotification(data.id);
			}
		} else if (notificationType === 'APPROVAL') {
			setApprovalId(data.notification.approvalId);
			setNotificationId(data.id);

			if (
				(data.notification.moduleName === 'EMPLOYEE'  ||
					data.notification.moduleName === 'TAXATION') &&
				!data.notification.isDelete
			) {
				setEmployeeNotificationModal(true);
			} else if (data.notification.moduleName === 'TIME_SHEETS') {
				data.notification.isDelete && setIsDelete(true);
				setEmployeeNotificationModal(true);
			} else if (data.notification.isEdit) {
				setEditNotificationModal(true);
			} else {
				if (data.notification.isDelete) {
					setDeleteNotificationModal(true);
				} else {
					setNotificationModalOpen(true);
				}
			}
			onClose();
		}
	};

	const dismissContractExpiryNotification = async (
		id: string,
		notification: any
	) => {
		setLoadingNotifications((prev) => ({ ...prev, [id]: true }));
		try {
			const response =
				await notificationApi.downloadContractExpiryExport(id);
			let expiryDate = 'contract_expiry';
			try {
				const parsed = JSON.parse(notification.message);
				if (parsed.expiryDate) {
					expiryDate = `contract_expiry_${parsed.expiryDate}`;
				}
			} catch {
				// use default filename
			}

			const url = window.URL.createObjectURL(
				new Blob([response.data], {
					type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				})
			);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `${expiryDate}.xlsx`);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);

			await notificationApi.dismissNotification({
				userNotificationId: id,
			});
			setNotifications((prevState: any) => ({
				...prevState,
				data: prevState.data.filter(
					(notification: any) => notification.id !== id
				),
				totalCount: prevState.totalCount - 1,
			}));
			toastText('Notification dismissed successfully', 'success');
		} catch (error: any) {
			const message =
				error?.response?.data?.message ||
				'Something went wrong in dismiss';
			toastText(message, 'error');
		} finally {
			setLoadingNotifications((prev) => ({ ...prev, [id]: false }));
		}
	};

	const dismissNhimaEligibilityNotification = async (
		id: string,
		notification: any
	) => {
		setLoadingNotifications((prev) => ({ ...prev, [id]: true }));
		try {
			const response =
				await notificationApi.downloadNhimaEligibilityExport(id);
			let eligibilityDate = 'nhima_eligibility';
			try {
				const parsed = JSON.parse(notification.message);
				if (parsed.eligibilityDate) {
					eligibilityDate = `nhima_eligibility_${parsed.eligibilityDate}`;
				}
			} catch {
				// use default filename
			}

			const url = window.URL.createObjectURL(
				new Blob([response.data], {
					type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
				})
			);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `${eligibilityDate}.xlsx`);
			document.body.appendChild(link);
			link.click();
			link.remove();
			window.URL.revokeObjectURL(url);

			await notificationApi.dismissNotification({
				userNotificationId: id,
			});
			setNotifications((prevState: any) => ({
				...prevState,
				data: prevState.data.filter(
					(notification: any) => notification.id !== id
				),
				totalCount: prevState.totalCount - 1,
			}));
			toastText('Notification dismissed successfully', 'success');
		} catch (error: any) {
			const message =
				error?.response?.data?.message ||
				'Something went wrong in dismiss';
			toastText(message, 'error');
		} finally {
			setLoadingNotifications((prev) => ({ ...prev, [id]: false }));
		}
	};

	const dismissNotification = async (id: string) => {
		setLoadingNotifications((prev) => ({ ...prev, [id]: true }));
		try {
			await notificationApi.dismissNotification({
				userNotificationId: id,
			});
			setNotifications((prevState: any) => ({
				...prevState,
				data: prevState.data.filter(
					(notification: any) => notification.id !== id
				),
				totalCount: prevState.totalCount - 1,
			}));

			toastText('Notification dismissed successfully', 'success');
		} catch (error: any) {
			const message =
				error?.response?.data?.message ||
				'Something went wrong in dismiss';
			toastText(message, 'error');
		} finally {
			setLoadingNotifications((prev) => ({ ...prev, [id]: false }));
		}
	};

	const renderActionButton = (data: any) => {
		const notificationType = data.notification.notificationType;
		if (!notificationType) return null;

		let action = '';
		if (data.notification.approvalId &&  notificationType === 'APPROVAL') {
			action = 'approve';
		} else {
			action = 'dismiss';
		}

		const buttonText = {
			dismiss: 'Dismiss',
			approve: 'View more',
		}[action];

		if (invalidText(buttonText)) return null;

		return (
			<Buttons
				buttons={[
					{
						text: buttonText!,
						isLoading: loadingNotifications[data.id] || false,
						className: 'btn-blue',
						isSubmit: false,
						disabled: false,
						onclick: () => {
							handleNotificationAction(data);
						},
						fontSize: '1.5rem',
						minWidth: '7rem',
						minHeight: '2rem',
					},
				]}
			/>
		);
	};

	return (
		<>
			<div className={`${styles['modal']} modal`}>
				<div className={styles['modal-header']}>
					<div className={styles['header-content']}>
						<Title level={4}>Notifications</Title>
						<span
							className={styles['show-all']}
							onClick={() => changePage()}
						>
							Show More
						</span>
					</div>
					<span
						className={styles['dismiss-all']}
						onClick={() => dismissAll()}
					>
						Dismiss All
					</span>
				</div>
				<div className={styles['modal-body']}>
					<SearchComponent
						placeHolder={'Search here...'}
						handleChange={handleSearch}
						handleChangeApi={() => {}}
						value={searchValue}
					/>
					{!loading && notifications?.length === 0 && (
						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								// marginTop: '2rem',
							}}
						>
							<div className={styles.emptyContainer}>
								<Empty
									image={<NoNotificationSvg />}
									imageStyle={{
										height: 60,
										width: 60,
									}}
									description={
										<div className={styles.description}>
											<h3 className={styles.title}>
												No Notifications
											</h3>
											<p className={styles.text}>
												You're all caught up! When you
												have new notifications, they
												will appear here.
											</p>
										</div>
									}
								/>
							</div>
						</div>
					)}
					{loading ? (
						<div
							style={{
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
								marginTop: '2rem',
							}}
						>
							<Loader />
						</div>
					) : (
						<div
							className={styles['modal-body--notification-list']}
						>
							{notifications?.map((notification) => {
								const isContractExpiry =
									notification.notification.isContractExpiry;
								const isNhimaEligibility =
									notification.notification.isNhimaEligibility;
								const isHighlighted =
									isContractExpiry || isNhimaEligibility;
								return (
								<div
									key={notification.id}
									className={`${
										styles[
											'modal-body--notification-list__item'
										]
									} ${
										isHighlighted
											? styles['contract-expiry-item']
											: ''
									}`}
								>
									<div
										className={`${styles['status-dot']} ${
											isHighlighted
												? styles['critical']
												: styles[
														notification.notification
															.notificationType
													]
										} ${
											styles[
												notification.notification
													.isDelete
													? 'delete'
													: ''
											]
										}`}
									/>
									<div className={styles['content']}>
										<div
											className={styles['content-header']}
										>
											<span className={styles['title']}>
												{
													notification.notification
														.title
												}
											</span>
											{isContractExpiry && (
												<span
													className={
														styles['contract-expiry-badge']
													}
												>
													Contract Expiry
												</span>
											)}
											{isNhimaEligibility && (
												<span
													className={
														styles['contract-expiry-badge']
													}
												>
													NHIMA Eligibility
												</span>
											)}
										</div>
										<div className={styles['meta']}>
											{isContractExpiry ? (
												<ContractExpiryMessageDisplay
													messageProp={JSON.parse(
														notification.notification
															.message
													)}
												/>
											) : isNhimaEligibility ? (
												<NhimaEligibilityMessageDisplay
													messageProp={JSON.parse(
														notification.notification
															.message
													)}
												/>
											) : (
												<StatusDisplay
													messageProp={JSON.parse(
														notification.notification
															.message
													)}
												/>
											)}
										</div>
										<div className={styles['action']}>
											{renderActionButton(notification)}
										</div>
									</div>
								</div>
							);
							})}
						</div>
					)}
				</div>
			</div>

			{notificationModalOpen && (
				<AddPushNotificationModal
					isNotificationModalOpen={notificationModalOpen}
					handleCancel={() => {
						setNotificationModalOpen(false);
					}}
					approvalId={approvalId}
					notificationId={notificationId}
				/>
			)}
			{deleteNotificationModal && (
				<DeletePushNotificationModal
					isDeleteNotificationModalOpen={deleteNotificationModal}
					handleCancel={() => {
						setDeleteNotificationModal(false);
					}}
					approvalId={approvalId}
					notificationId={notificationId}
				/>
			)}
			{employeeNotificationModal && (
				<EmployeeNotificationModal
					isNotificationModalOpen={employeeNotificationModal}
					handleCancel={() => {
						setEmployeeNotificationModal(false);
					}}
					approvalId={approvalId}
					isDelete={isDelete}
					notificationId={notificationId}
				/>
			)}
			{editNotificationModal && (
				<UpdateNotificationModal
					isNotificationModalOpen={editNotificationModal}
					handleCancel={() => {
						setEditNotificationModal(false);
					}}
					approvalId={approvalId}
					notificationId={notificationId}
				/>
			)}
		</>
	);
};

export default Notifications;

const ContractExpiryMessageDisplay = ({ messageProp }: any) => {
	const { createdBy, expiryDate } = messageProp;

	const formatExpiryDate = (dateStr: string) => {
		const parts = dateStr.split('-');
		if (parts.length === 3) {
			return `${parts[2]}/${parts[1]}/${parts[0]}`;
		}
		return dateStr;
	};

	return (
		<div className={styles['notification_message']}>
			<span>Contract expiry date: </span>
			<span>{expiryDate ? formatExpiryDate(expiryDate) : 'N/A'}</span>
			<span> — notified by </span>
			<span>{createdBy}</span>
		</div>
	);
};

const NhimaEligibilityMessageDisplay = ({ messageProp }: any) => {
	const { createdBy, eligibilityDate } = messageProp;

	const formatEligibilityDate = (dateStr: string) => {
		const parts = dateStr.split('-');
		if (parts.length === 3) {
			return `${parts[2]}/${parts[1]}/${parts[0]}`;
		}
		return dateStr;
	};

	return (
		<div className={styles['notification_message']}>
			<span>Turned 18 on: </span>
			<span>
				{eligibilityDate
					? formatEligibilityDate(eligibilityDate)
					: 'N/A'}
			</span>
			<span> — notified by </span>
			<span>{createdBy}</span>
		</div>
	);
};

const StatusDisplay = ({ messageProp }: any) => {
	const {
		createdAt,
		createdBy,
		eventType,
		approvedBy,
		rejectedBy,
		deletedBy,
	} = messageProp;

	// Format date
	const date = new Date(createdAt);
	const formattedDate = `${date.getDate().toString().padStart(2, '0')}/${(
		date.getMonth() + 1
	)
		.toString()
		.padStart(2, '0')}/${date.getFullYear()}`;

	// Format time
	const hours = date.getHours();
	const minutes = date.getMinutes();
	const ampm = hours >= 12 ? 'PM' : 'AM';
	const formattedHours = hours % 12 || 12; // Convert 0 to 12
	const formattedTime = `${formattedHours}:${minutes
		.toString()
		.padStart(2, '0')} ${ampm}`;

	return (
		<div className={styles['notification_message']}>
			{eventType === 'ADD' && <span>Added by </span>}
			{eventType === 'EDIT' && <span>Edited by </span>}
			{eventType === 'DELETE' && <span>Deleted by </span>}
			<span>{createdBy} </span>
			<span>on </span>
			<span>{formattedDate} </span>
			<span>at </span>
			<span>{formattedTime} </span>
			{approvedBy && (
				<>
					<span>Approved by </span>
					<span>{approvedBy}</span>
				</>
			)}
			{rejectedBy && (
				<>
					<span>Rejected by </span>
					<span>{rejectedBy}</span>
				</>
			)}
			{deletedBy && (
				<>
					<span>Deleted by </span>
					<span>{deletedBy}</span>
				</>
			)}
		</div>
	);
};
